import { useState, useEffect } from "react";
import {
  Mail, Phone, ExternalLink, Download,
  Trophy, FileText, ChevronUp, Sun, Moon,
  X, ArrowUpRight, Award
} from "lucide-react";
import { FaGithub, FaLinkedin, FaGoodreads } from "react-icons/fa";
import { FaMedium, FaXTwitter, FaLetterboxd } from "react-icons/fa6";

/* ─────────────────────────────────────────
   GLOBAL STYLES
───────────────────────────────────────── */
const style = `
  @import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg:        #f8f7f4;
    --bg2:       #f0ede8;
    --bg3:       #e8e4de;
    --bg4:       #dedad3;
    --text:      #0d0d0d;
    --text-muted:#5a5650;
    --text-light:#9a9690;
    --accent:    #16a34a;
    --accent-hi: #22c55e;
    --border:    #e0dbd4;
    --shadow:    rgba(0,0,0,0.07);
    --nav-bg:    rgba(248,247,244,0.88);
    --font:      'Geist', -apple-system, 'Helvetica Neue', sans-serif;
  }

  [data-theme="dark"] {
    --bg:        #0c0c0c;
    --bg2:       #141414;
    --bg3:       #1c1c1c;
    --bg4:       #242424;
    --text:      #ededed;
    --text-muted:#888880;
    --text-light:#4a4a44;
    --accent:    #22c55e;
    --accent-hi: #4ade80;
    --border:    #222220;
    --shadow:    rgba(0,0,0,0.45);
    --nav-bg:    rgba(12,12,12,0.88);
  }

  html { scroll-behavior: smooth; }
  body {
    background: var(--bg); color: var(--text);
    font-family: var(--font); font-size: 15.5px; line-height: 1.72;
    -webkit-font-smoothing: antialiased;
    transition: background 0.3s, color 0.3s;
  }
  ::selection { background: #22c55e28; }
  a { color: inherit; text-decoration: none; transition: color 0.15s; }
  .container { max-width: 920px; margin: 0 auto; padding: 0 24px; }

  /* ── NAV ── */
  nav {
    position: sticky; top: 0; z-index: 200;
    background: var(--nav-bg);
    backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px);
    border-bottom: 1px solid var(--border);
    transition: background 0.3s, border-color 0.3s;
  }
  .nav-inner { display: flex; justify-content: space-between; align-items: center; height: 50px; }
  .nav-name { font-size: 0.88rem; font-weight: 500; letter-spacing: -0.02em; }
  .nav-right { display: flex; align-items: center; gap: 4px; }
  .nav-links { display: flex; gap: 2px; }
  .nav-links a {
    font-size: 0.79rem; color: var(--text-light);
    padding: 5px 9px; border-radius: 5px;
    transition: color 0.15s, background 0.15s;
  }
  .nav-links a:hover { color: var(--text); background: var(--bg3); }
  .nav-btn {
    background: none; border: none; cursor: pointer;
    color: var(--text-muted); padding: 7px; border-radius: 5px;
    display: flex; align-items: center;
    transition: color 0.15s, background 0.15s;
  }
  .nav-btn:hover { color: var(--text); background: var(--bg3); }

  /* hamburger */
  .hamburger-btn {
    display: none; flex-direction: column; justify-content: center; align-items: center;
    gap: 5px; background: none; border: none; cursor: pointer;
    padding: 6px; border-radius: 5px; width: 32px; height: 32px;
  }
  .hamburger-btn span {
    display: block; width: 18px; height: 1.5px;
    background: var(--text-muted); border-radius: 2px;
    transition: transform 0.35s cubic-bezier(.23,1,.32,1), opacity 0.22s, width 0.28s;
    transform-origin: center;
  }
  .hamburger-btn:hover span { background: var(--text); }
  .hamburger-btn.open span:nth-child(1) { transform: translateY(6.5px) rotate(45deg); }
  .hamburger-btn.open span:nth-child(2) { opacity: 0; width: 0; }
  .hamburger-btn.open span:nth-child(3) { transform: translateY(-6.5px) rotate(-45deg); }

  .mobile-menu {
    position: absolute; top: 50px; left: 0; right: 0;
    background: var(--bg); border-bottom: 1px solid var(--border);
    padding: 6px 0 10px;
    transform: translateY(-10px); opacity: 0; pointer-events: none;
    transition: transform 0.3s cubic-bezier(.23,1,.32,1), opacity 0.22s;
  }
  .mobile-menu.open { transform: translateY(0); opacity: 1; pointer-events: all; }
  .mobile-menu a {
    display: block; padding: 9px 24px; font-size: 0.85rem;
    color: var(--text-muted); transition: color 0.15s, background 0.15s;
  }
  .mobile-menu a:hover { color: var(--text); background: var(--bg2); }

  @media (max-width: 620px) {
    .hamburger-btn { display: flex; }
    .nav-links { display: none; }
  }

  /* ── HERO ── */
  .hero { padding: 78px 0 62px; }
  .hero-row { display: flex; align-items: center; gap: 18px; margin-bottom: 22px; }
  .hero-avatar {
    width: 68px; height: 68px; border-radius: 50%;
    object-fit: cover; border: 1.5px solid var(--border); flex-shrink: 0;
    cursor: pointer; transition: transform 0.2s, box-shadow 0.2s;
  }
  .hero-avatar:hover { transform: scale(1.05); box-shadow: 0 4px 14px var(--shadow); }
  .hero-name { font-size: 1.6rem; font-weight: 600; letter-spacing: -0.04em; line-height: 1.15; margin-bottom: 3px; }
  .hero-sub { font-size: 0.82rem; color: var(--text-light); letter-spacing: 0.01em; }
  .hero-bio p { font-size: 0.93rem; color: var(--text-muted); line-height: 1.78; margin-bottom: 11px; font-weight: 300; }
  .hero-bio p:last-child { margin-bottom: 0; }
  .hero-bio a { color: var(--text); border-bottom: 1px solid var(--border); transition: color 0.15s, border-color 0.15s; }
  .hero-bio a:hover { color: var(--accent); border-bottom-color: var(--accent-hi); }
  .currently-reading { margin-top: 18px; font-size: 0.81rem; color: var(--text-light); font-weight: 300; }
  .currently-reading em { font-style: normal; color: var(--text-muted); }
  .hero-actions { display: flex; align-items: center; gap: 11px; margin-top: 26px; flex-wrap: wrap; }
  .btn-resume {
    display: inline-flex; align-items: center; gap: 6px;
    background: var(--text); color: var(--bg);
    font-size: 0.77rem; font-weight: 500;
    padding: 7px 15px; border-radius: 5px;
    transition: background 0.18s, transform 0.15s;
  }
  .btn-resume:hover { background: var(--accent); transform: translateY(-1px); }
  .hero-socials { display: flex; gap: 3px; }
  .hero-socials a {
    color: var(--text-light); padding: 7px; border-radius: 5px; display: flex;
    transition: color 0.15s, background 0.15s, transform 0.15s;
  }
  .hero-socials a:hover { color: var(--text); background: var(--bg3); transform: translateY(-1px); }

  hr { border: none; border-top: 1px solid var(--border); }
  section { padding: 50px 0; }
  .section-label {
    font-size: 0.69rem; font-weight: 500; letter-spacing: 0.13em;
    text-transform: uppercase; color: var(--text-light); margin-bottom: 22px;
  }

  /* ── RANK BADGES ── */
  .rank-badge {
    font-size: 0.64rem; font-weight: 500;
    padding: 2px 8px; border-radius: 20px; border: 1px solid transparent;
    letter-spacing: 0.02em; display: inline-block;
  }
  .rank-legend    { background:#fef9c3; color:#854d0e;  border-color:#fef08a; }
  .rank-grandmaster { background:#fee2e2; color:#991b1b;  border-color:#fca5a5; }
  .rank-master    { background:#fce7f3; color:#9d174d;  border-color:#f9a8d4; }
  .rank-knight    { background:#ede9fe; color:#7c3aed;  border-color:#c4b5fd; }
  .rank-expert    { background:#dbeafe; color:#1d4ed8;  border-color:#93c5fd; }
  .rank-pupil     { background:#cffafe; color:#0e7490;  border-color:#67e8f9; }
  .rank-newbie    { background:#dcfce7; color:#15803d;  border-color:#86efac; }
  .rank-div2      { background:#dbeafe; color:#1d4ed8;  border-color:#93c5fd; }
  .rank-div3      { background:#dcfce7; color:#15803d;  border-color:#86efac; }
  .rank-4star     { background:#fef3c7; color:#b45309;  border-color:#fcd34d; }
  .rank-3star     { background:#cffafe; color:#0e7490;  border-color:#67e8f9; }
  .rank-100days   { background:#fef3c7; color:#b45309;  border-color:#fcd34d; }
  .rank-streak    { background:#ffe4e6; color:#be123c;  border-color:#fda4af; }
  .rank-top3      { background:#ede9fe; color:#7c3aed;  border-color:#c4b5fd; }
  .rank-default   { background:var(--bg3); color:var(--text-light); border-color:var(--border); }

  [data-theme="dark"] .rank-legend    { background:#422006; color:#fde68a; border-color:#78350f; }
  [data-theme="dark"] .rank-grandmaster { background:#450a0a; color:#fca5a5; border-color:#7f1d1d; }
  [data-theme="dark"] .rank-master    { background:#500724; color:#f9a8d4; border-color:#831843; }
  [data-theme="dark"] .rank-knight    { background:#2e1065; color:#c084fc; border-color:#6d28d9; }
  [data-theme="dark"] .rank-expert    { background:#1e3a5f; color:#60a5fa; border-color:#1d4ed8; }
  [data-theme="dark"] .rank-pupil     { background:#083344; color:#22d3ee; border-color:#0e7490; }
  [data-theme="dark"] .rank-newbie    { background:#14532d; color:#4ade80; border-color:#166534; }
  [data-theme="dark"] .rank-div2      { background:#1e3a5f; color:#60a5fa; border-color:#1d4ed8; }
  [data-theme="dark"] .rank-div3      { background:#14532d; color:#4ade80; border-color:#166534; }
  [data-theme="dark"] .rank-4star     { background:#451a03; color:#fbbf24; border-color:#92400e; }
  [data-theme="dark"] .rank-3star     { background:#083344; color:#22d3ee; border-color:#0e7490; }
  [data-theme="dark"] .rank-100days   { background:#451a03; color:#fbbf24; border-color:#92400e; }
  [data-theme="dark"] .rank-streak    { background:#4c0519; color:#fb7185; border-color:#9f1239; }
  [data-theme="dark"] .rank-top3      { background:#2e1065; color:#c084fc; border-color:#6d28d9; }

  /* ── PROFILE CARDS ── */
  .profiles-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .profile-card {
    border: 1px solid var(--border); padding: 15px 16px;
    background: var(--bg2); border-radius: 8px;
    cursor: pointer; color: inherit;
    transition: border-color 0.2s, background 0.2s, transform 0.2s, box-shadow 0.2s;
  }
  .profile-card:hover { border-color: var(--accent); background: var(--bg3); transform: translateY(-2px); box-shadow: 0 6px 22px var(--shadow); }
  .profile-card-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 5px; }
  .profile-platform-row { display: flex; align-items: center; gap: 6px; }
  .profile-platform { font-size: 0.68rem; font-weight: 500; letter-spacing: 0.07em; text-transform: uppercase; color: var(--text-light); }
  .profile-handle { font-size: 0.87rem; font-weight: 500; margin-bottom: 7px; }
  .profile-stats { display: flex; gap: 10px; flex-wrap: wrap; }
  .profile-stat { font-size: 0.73rem; color: var(--text-muted); }
  .profile-stat strong { color: var(--text); font-weight: 500; }
  .profile-badges { display: flex; gap: 5px; flex-wrap: wrap; margin-top: 7px; }

  /* ── PROFILE POPUP ── */
  .popup-overlay {
    position: fixed; inset: 0; z-index: 500;
    background: rgba(0,0,0,0.42); backdrop-filter: blur(5px);
    display: flex; align-items: center; justify-content: center;
    opacity: 0; pointer-events: none; transition: opacity 0.22s;
  }
  .popup-overlay.open { opacity: 1; pointer-events: all; }
  .popup-box {
    background: var(--bg); border: 1px solid var(--border); border-radius: 16px;
    padding: 26px 26px 22px; width: min(400px, 92vw);
    box-shadow: 0 24px 64px rgba(0,0,0,0.18);
    transform: scale(0.9) translateY(14px); opacity: 0;
    transition: transform 0.32s cubic-bezier(.23,1,.32,1), opacity 0.22s;
    position: relative;
  }
  .popup-overlay.open .popup-box { transform: scale(1) translateY(0); opacity: 1; }
  
  /* ── AVATAR POPUP ── */
  .avatar-popup { border-radius: 50%; width: min(300px, 88vw); height: min(300px, 88vw); padding: 0; overflow: hidden; }
  .avatar-popup img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
  .popup-close {
    position: absolute; top: 13px; right: 13px;
    background: var(--bg3); border: none; border-radius: 50%;
    width: 27px; height: 27px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    color: var(--text-muted); transition: background 0.15s, color 0.15s;
  }
  .popup-close:hover { background: var(--bg4); color: var(--text); }
  .popup-logo {
    width: 42px; height: 42px; border-radius: 50%;
    background: var(--bg3); border: 1px solid var(--border);
    display: flex; align-items: center; justify-content: center; margin-bottom: 13px;
  }
  .popup-plat-name { font-size: 0.68rem; font-weight: 500; letter-spacing: 0.1em; text-transform: uppercase; color: var(--text-light); margin-bottom: 3px; }
  .popup-handle { font-size: 1.12rem; font-weight: 600; letter-spacing: -0.02em; margin-bottom: 15px; }
  .popup-stats-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 9px; margin-bottom: 14px; }
  .popup-stat-box { background: var(--bg2); border: 1px solid var(--border); border-radius: 8px; padding: 11px 13px; }
  .popup-stat-value { font-size: 1.25rem; font-weight: 600; letter-spacing: -0.03em; line-height: 1.1; }
  .popup-stat-label { font-size: 0.7rem; color: var(--text-light); margin-top: 2px; font-weight: 300; }
  .popup-badges { display: flex; gap: 5px; flex-wrap: wrap; margin-bottom: 13px; }
  .popup-extra { font-size: 0.79rem; color: var(--text-muted); font-weight: 300; margin-bottom: 15px; line-height: 1.6; }
  .popup-visit {
    display: flex; align-items: center; justify-content: center; gap: 6px;
    background: var(--text); color: var(--bg);
    font-size: 0.79rem; font-weight: 500;
    padding: 8px 14px; border-radius: 6px; width: 100%;
    transition: background 0.15s;
  }
  .popup-visit:hover { background: var(--accent); }

  /* ── PROJECT POPUP ── */
  .project-popup-name { font-size: 1.15rem; font-weight: 600; margin-bottom: 12px; letter-spacing: -0.02em; }
  .project-popup-desc { font-size: 0.84rem; color: var(--text-muted); line-height: 1.7; margin-bottom: 14px; font-weight: 300; }
  .project-screenshots { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; }
  .project-screenshot {
    width: 100%; height: 120px; object-fit: cover; border-radius: 8px;
    border: 1px solid var(--border); transition: transform 0.2s, box-shadow 0.2s;
  }
  .project-screenshot:hover { transform: scale(1.02); box-shadow: 0 4px 12px var(--shadow); }

  /* ── EXPERIENCE POPUP ── */
  .exp-popup-role { font-size: 1.15rem; font-weight: 600; margin-bottom: 6px; letter-spacing: -0.02em; }
  .exp-popup-company { font-size: 0.82rem; color: var(--text-light); margin-bottom: 14px; font-weight: 300; }
  .exp-popup-desc { font-size: 0.84rem; color: var(--text-muted); line-height: 1.7; margin-bottom: 14px; font-weight: 300; }

  /* ── CERT POPUP ── */
  .cert-overlay {
    position: fixed; inset: 0; z-index: 500;
    background: rgba(0,0,0,0.5); backdrop-filter: blur(5px);
    display: flex; align-items: center; justify-content: center;
    opacity: 0; pointer-events: none; transition: opacity 0.22s;
  }
  .cert-overlay.open { opacity: 1; pointer-events: all; }
  .cert-box {
    background: var(--bg); border: 1px solid var(--border); border-radius: 14px;
    padding: 22px; width: min(520px, 94vw);
    box-shadow: 0 24px 64px rgba(0,0,0,0.22);
    transform: scale(0.9) translateY(16px); opacity: 0;
    transition: transform 0.32s cubic-bezier(.23,1,.32,1), opacity 0.22s;
    position: relative;
    max-height: 92vh; overflow-y: auto;
  }
  .cert-overlay.open .cert-box { transform: scale(1) translateY(0); opacity: 1; }
  .cert-icon {
    width: 44px; height: 44px; border-radius: 10px;
    background: var(--bg3); border: 1px solid var(--border);
    display: flex; align-items: center; justify-content: center;
    color: var(--accent); margin-bottom: 13px;
  }
  .cert-pop-title { font-size: 1.02rem; font-weight: 600; letter-spacing: -0.02em; margin-bottom: 3px; }
  .cert-pop-issuer { font-size: 0.8rem; color: var(--text-muted); font-weight: 300; margin-bottom: 16px; }

  /* certificate embed area */
  .cert-embed-wrap {
    width: 100%; border-radius: 10px; overflow: hidden;
    border: 1px solid var(--border); background: var(--bg2);
    margin-bottom: 14px; position: relative;
  }
  .cert-embed-wrap iframe {
    width: 100%; height: 300px; border: none; display: block;
  }
  .cert-embed-placeholder {
    width: 100%; height: 180px; display: flex; flex-direction: column;
    align-items: center; justify-content: center; gap: 8px;
    color: var(--text-light); font-size: 0.78rem; font-weight: 300;
  }
  .cert-embed-placeholder svg { opacity: 0.35; }

  .cert-actions { display: flex; gap: 7px; }
  .cert-btn-pri {
    flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 5px;
    background: var(--text); color: var(--bg);
    font-size: 0.78rem; font-weight: 500; padding: 8px 14px; border-radius: 6px;
    transition: background 0.15s;
  }
  .cert-btn-pri:hover { background: var(--accent); }
  .cert-btn-sec {
    flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 5px;
    background: var(--bg2); color: var(--text-muted);
    border: 1px solid var(--border);
    font-size: 0.78rem; font-weight: 400; padding: 8px 14px; border-radius: 6px;
    cursor: pointer; font-family: var(--font);
    transition: color 0.15s, border-color 0.15s;
  }
  .cert-btn-sec:hover { color: var(--text); border-color: var(--text-muted); }

  /* ── SKILLS ── */
  .skills-group { margin-bottom: 25px; }
  .skills-group:last-child { margin-bottom: 0; }
  .skills-group-label { font-size: 0.68rem; font-weight: 500; letter-spacing: 0.09em; text-transform: uppercase; color: var(--text-light); margin-bottom: 9px; }
  .skills-list { display: flex; flex-wrap: wrap; gap: 7px; }
  .skill-chip {
    display: inline-flex; align-items: center; gap: 5px;
    font-size: 0.77rem; color: var(--text-muted);
    background: var(--bg2); border: 1px solid var(--border);
    padding: 4px 10px; border-radius: 5px;
    transition: border-color 0.15s, color 0.15s, background 0.15s;
  }
  .skill-chip:hover { border-color: var(--accent); color: var(--text); background: var(--bg3); }
  .skill-icon { width: 13px; height: 13px; opacity: 0.55; }
  [data-theme="dark"] .skill-icon { filter: invert(1); opacity: 0.45; }

  /* ── EXPERIENCE ── */
  .entry { display: grid; grid-template-columns: 105px 1fr; gap: 18px; margin-bottom: 26px; }
  .entry:last-child { margin-bottom: 0; }
  .entry-date { font-size: 0.72rem; color: var(--text-light); font-weight: 300; line-height: 1.5; }
  .entry-company { font-size: 0.72rem; color: var(--text-light); font-weight: 300; margin-top: 2px; }
  .entry-role { font-size: 0.92rem; font-weight: 500; margin-bottom: 5px; }
  .entry-desc { font-size: 0.85rem; color: var(--text-muted); line-height: 1.72; margin-bottom: 8px; font-weight: 300; }
  .entry-cert {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 0.73rem; color: var(--accent); cursor: pointer;
    background: none; border: none; padding: 0; font-family: var(--font);
    transition: color 0.15s;
  }
  .entry-cert:hover { color: var(--accent-hi); }
  .entry-details-btn {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 0.73rem; color: var(--accent); cursor: pointer;
    background: none; border: none; padding: 0; font-family: var(--font);
    transition: color 0.15s;
  }
  .entry-details-btn:hover { color: var(--accent-hi); }

  /* ── PROJECTS ── */
  .project-item { padding: 17px 0; border-bottom: 1px solid var(--border); }
  .project-item:first-child { padding-top: 0; }
  .project-item:last-child { border-bottom: none; padding-bottom: 0; }
  .project-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 4px; }
  .project-name { font-size: 0.92rem; font-weight: 500; display: flex; align-items: center; gap: 8px; }
  .ongoing-badge { display: flex; align-items: center; gap: 4px; font-size: 0.7rem; color: var(--text-light); font-weight: 400; }
  .ongoing-dot { width: 6px; height: 6px; background: #22c55e; border-radius: 50%; }
  .project-links { display: flex; gap: 10px; }
  .project-link { display: inline-flex; align-items: center; gap: 3px; font-size: 0.72rem; color: var(--text-light); transition: color 0.15s; }
  .project-link:hover { color: var(--accent); }
  .project-desc { font-size: 0.84rem; color: var(--text-muted); line-height: 1.7; margin-bottom: 9px; font-weight: 300; }
  .project-stack { display: flex; gap: 5px; flex-wrap: wrap; margin-bottom: 8px; }
  .stack-tag { font-size: 0.67rem; padding: 2px 8px; border: 1px solid var(--border); border-radius: 4px; color: var(--text-light); background: var(--bg2); }
  .project-details-btn {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 0.73rem; color: var(--accent); cursor: pointer;
    background: none; border: none; padding: 0; font-family: var(--font);
    transition: color 0.15s;
  }
  .project-details-btn:hover { color: var(--accent-hi); }

  /* ── EDUCATION ── */
  .edu-item { display: grid; grid-template-columns: 105px 1fr; gap: 18px; margin-bottom: 20px; }
  .edu-item:last-child { margin-bottom: 0; }
  .edu-year { font-size: 0.72rem; color: var(--text-light); font-weight: 300; padding-top: 2px; }
  .edu-degree { font-size: 0.92rem; font-weight: 500; margin-bottom: 2px; }
  .edu-school { font-size: 0.82rem; color: var(--text-muted); font-weight: 300; margin-bottom: 2px; }
  .edu-detail { font-size: 0.77rem; color: var(--text-light); font-weight: 300; }

  /* ── ACHIEVEMENTS ── */
  .ach-item { display: flex; gap: 12px; margin-bottom: 17px; align-items: flex-start; }
  .ach-item:last-child { margin-bottom: 0; }
  .ach-icon { color: var(--text-light); margin-top: 2px; flex-shrink: 0; }
  .ach-title { font-size: 0.89rem; font-weight: 500; margin-bottom: 2px; }
  .ach-desc { font-size: 0.81rem; color: var(--text-muted); line-height: 1.62; font-weight: 300; }
  .ach-year { font-size: 0.69rem; color: var(--text-light); margin-top: 2px; }

  /* ── CERTIFICATIONS ── */
  .cert-item { display: grid; grid-template-columns: 105px 1fr; gap: 18px; margin-bottom: 17px; }
  .cert-item:last-child { margin-bottom: 0; }
  .cert-year { font-size: 0.72rem; color: var(--text-light); font-weight: 300; padding-top: 2px; }
  .cert-name { font-size: 0.89rem; font-weight: 500; margin-bottom: 1px; }
  .cert-issuer { font-size: 0.79rem; color: var(--text-muted); font-weight: 300; }
  .cert-trigger {
    display: inline-flex; align-items: center; gap: 3px;
    font-size: 0.71rem; color: var(--accent); margin-top: 4px;
    cursor: pointer; background: none; border: none; padding: 0; font-family: var(--font);
    transition: color 0.15s;
  }
  .cert-trigger:hover { color: var(--accent-hi); }

  /* ── BLOG ── */
  .blog-item { display: flex; justify-content: space-between; align-items: baseline; padding: 9px 0; border-bottom: 1px solid var(--border); }
  .blog-item:last-child { border-bottom: none; }
  .blog-date { font-size: 0.72rem; color: var(--text-light); font-weight: 300; white-space: nowrap; margin-left: 16px; flex-shrink: 0; }
  .blog-title { font-size: 0.87rem; color: var(--text-muted); display: inline-flex; align-items: center; gap: 4px; transition: color 0.15s; }
  .blog-title .arrow { opacity: 0; transform: translateX(-3px); transition: opacity 0.15s, transform 0.15s; }
  .blog-title:hover { color: var(--accent); }
  .blog-title:hover .arrow { opacity: 1; transform: translateX(0); }

  /* ── HOBBIES ── */
  .hobbies-text p { font-size: 0.91rem; color: var(--text-muted); line-height: 1.8; margin-bottom: 11px; font-weight: 300; }
  .hobbies-text p:last-child { margin-bottom: 0; }
  .hobbies-text a { color: var(--text); border-bottom: 1px solid var(--border); transition: color 0.15s, border-color 0.15s; }
  .hobbies-text a:hover { color: var(--accent); border-bottom-color: var(--accent-hi); }

  /* ── CONTACT ── */
  .contact-links { display: flex; flex-direction: column; gap: 8px; }
  .contact-row { display: flex; align-items: center; gap: 10px; font-size: 0.87rem; color: var(--text-muted); font-weight: 300; }
  .contact-row svg { color: var(--text-light); flex-shrink: 0; }
  .contact-row a { color: var(--text-muted); transition: color 0.15s; }
  .contact-row a:hover { color: var(--accent); }

  /* ── FOOTER ── */
  footer { border-top: 1px solid var(--border); background: var(--bg2); padding: 36px 0 32px; transition: background 0.3s; }
  .footer-inner { display: flex; justify-content: space-between; align-items: flex-end; flex-wrap: wrap; gap: 22px; }
  .footer-name { font-size: 0.98rem; font-weight: 600; letter-spacing: -0.03em; margin-bottom: 3px; }
  .footer-tagline { font-size: 0.76rem; color: var(--text-light); font-weight: 300; }
  .footer-links { display: flex; gap: 5px; margin-top: 13px; flex-wrap: wrap; }
  .footer-link {
    display: inline-flex; align-items: center; gap: 5px;
    font-size: 0.75rem; color: var(--text-light);
    padding: 5px 10px; border: 1px solid var(--border); border-radius: 5px; background: var(--bg3);
    transition: color 0.15s, border-color 0.15s, transform 0.15s;
  }
  .footer-link:hover { color: var(--text); border-color: var(--text-muted); transform: translateY(-1px); }
  .footer-right { text-align: right; }
  .footer-copy { font-size: 0.73rem; color: var(--text-light); font-weight: 300; }
  .footer-status { display: flex; align-items: center; gap: 6px; font-size: 0.73rem; color: var(--text-light); margin-top: 6px; justify-content: flex-end; }
  .status-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--accent); flex-shrink: 0; animation: pulse 2.4s ease-in-out infinite; }
  @keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.3;transform:scale(0.8)} }

  /* ── BACK TO TOP ── */
  .back-top {
    position: fixed; bottom: 22px; right: 22px;
    background: var(--text); color: var(--bg);
    border: none; border-radius: 6px; width: 32px; height: 32px;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; opacity: 0; transform: translateY(8px);
    transition: opacity 0.25s, transform 0.25s, background 0.15s; pointer-events: none;
  }
  .back-top.visible { opacity: 1; transform: translateY(0); pointer-events: all; }
  .back-top:hover { background: var(--accent); }

  @media (max-width: 620px) {
    .profiles-grid { grid-template-columns: 1fr; }
    .entry, .edu-item, .cert-item { grid-template-columns: 1fr; gap: 4px; }
    .hero { padding: 54px 0 46px; }
    .footer-inner { flex-direction: column; align-items: flex-start; }
    .footer-right { text-align: left; }
    .footer-status { justify-content: flex-start; }
    .cert-embed-wrap iframe { height: 220px; }
  }
`;

/* ── Badge classifier ── */
function rankClass(badge) {
  const b = badge.toLowerCase();
  if (b.includes("legend"))       return "rank-legend";
  if (b.includes("grandmaster"))  return "rank-grandmaster";
  if (b.includes("master"))       return "rank-master";
  if (b.includes("knight"))       return "rank-knight";
  if (b.includes("expert"))       return "rank-expert";
  if (b.includes("pupil"))        return "rank-pupil";
  if (b.includes("newbie"))       return "rank-newbie";
  if (b.includes("achiever"))     return "rank-newbie";
  if (b.includes("questions"))    return "rank-newbie";
  if (b.includes("div. 2") || b.includes("div.2") || b.includes("div 2")) return "rank-div2";
  if (b.includes("div. 3") || b.includes("div.3") || b.includes("div 3")) return "rank-div3";
  if (b.includes("5★") || b.includes("4 star") || b.includes("4-star")) return "rank-4star";
  if (b.includes("4★") || b.includes("4 star") || b.includes("4-star")) return "rank-4star";
  if (b.includes("3★") || b.includes("3 star") || b.includes("3-star")) return "rank-3star";
  if (b.includes("100"))          return "rank-100days";
  if (b.includes("streak"))       return "rank-streak";
  if (b.includes("rank #") || b.includes("#1") || b.includes("#2") || b.includes("#3")) return "rank-top3";
  return "rank-default";
}

/* ── Converts any Google Drive share link to an embeddable preview URL ── */
function toEmbedUrl(link) {
  if (!link || link === "#") return null;
  // Google Drive share → embed/preview
  const driveMatch = link.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (driveMatch) return `https://drive.google.com/file/d/${driveMatch[1]}/preview`;
  // Already an embed/preview
  if (link.includes("drive.google.com/file/d/") && link.includes("/preview")) return link;
  // Coursera, Credly, etc. — open directly (many block iframe; we try anyway)
  return link;
}

/* ── DATA ── */
const TechChip = ({ name, src }) => (
  <span className="skill-chip">
    <img src={src} alt={name} className="skill-icon" />
    {name}
  </span>
);

const skillGroups = [
  { label: "Programming Languages", skills: [
    { name: "C++",         src: "https://cdn.simpleicons.org/cplusplus/444" },
    { name: "C",           src: "https://cdn.simpleicons.org/c/444" },
    { name: "Python",      src: "https://cdn.simpleicons.org/python/444" },
    { name: "JavaScript",  src: "https://cdn.simpleicons.org/javascript/444" },
  ]},
  { label: "Full Stack", skills: [
    { name: "React",       src: "https://cdn.simpleicons.org/react/444" },
    { name: "Node.js",     src: "https://cdn.simpleicons.org/nodedotjs/444" },
    { name: "TypeScript",  src: "https://cdn.simpleicons.org/typescript/444" },
    { name: "Next.js",     src: "https://cdn.simpleicons.org/nextdotjs/444" },
    { name: "MongoDB",     src: "https://cdn.simpleicons.org/mongodb/444" },
    { name: "PostgreSQL",  src: "https://cdn.simpleicons.org/postgresql/444" },
    { name: "Express",     src: "https://cdn.simpleicons.org/express/444" },
    { name: "Tailwind CSS",src: "https://cdn.simpleicons.org/tailwindcss/444" },
    { name: "CSS",         src: "https://cdn.simpleicons.org/css/444" },
  ]},
  { label: "Data Science & ML", skills: [
    { name: "TensorFlow",  src: "https://cdn.simpleicons.org/tensorflow/444" },
    { name: "PyTorch",     src: "https://cdn.simpleicons.org/pytorch/444" },
    { name: "Pandas",      src: "https://cdn.simpleicons.org/pandas/444" },
    { name: "NumPy",       src: "https://cdn.simpleicons.org/numpy/444" },
    { name: "Scikit-learn",src: "https://cdn.simpleicons.org/scikitlearn/444" },
  ]},
  { label: "Tools & Platforms", skills: [
    { name: "GitHub",      src: "https://cdn.simpleicons.org/github/444" },
    { name: "Git",         src: "https://cdn.simpleicons.org/git/444" },
    { name: "Streamlit",   src: "https://cdn.simpleicons.org/streamlit/444" },
    { name: "Docker",      src: "https://cdn.simpleicons.org/docker/444" },
    { name: "Linux",       src: "https://cdn.simpleicons.org/linux/444" },
    { name: "Figma",       src: "https://cdn.simpleicons.org/figma/444" },
  ]},
];

const profiles = [
  { platform: "LeetCode", handle: "@Pratyay19", url: "https://leetcode.com/u/Pratyay19/",
    icon: "https://cdn.simpleicons.org/leetcode",
    stats: [{ label: "Solved", value: "500+" }, { label: "Rating", value: "1720" }],
    badges: ["60+ Days Streak"],
    extra: "Global rank ~100,000 · Top 11.85% overall · 60+ Days Streak" },
  { platform: "Codeforces", handle: "@Pratyay19", url: "https://codeforces.com/profile/Pratyay19",
    icon: "https://cdn.simpleicons.org/codeforces",
    stats: [{ label: "Rating", value: "1364" }, { label: "Rank", value: "Pupil" }],
    badges: ["150+ questions", "Div. 2"],
    extra: "Active since 2025 · 8+ contests · Institute rank #4" },
  { platform: "GeeksforGeeks", handle: "@pratyay19", url: "https://www.geeksforgeeks.org/profile/pratyay19",
    icon: "https://cdn.simpleicons.org/geeksforgeeks",
    stats: [{ label: "Score", value: "700+" }, { label: "Solved", value: "300+" }],
    badges: ["Rank ~1000"],
    extra: "Institute rank ~1000 of 6000+ students" },
  { platform: "CodeChef", handle: "@pratyaydas", url: "https://www.codechef.com/users/pratyay19",
    icon: "https://cdn.simpleicons.org/codechef",
    stats: [{ label: "Rating", value: "1792" }, { label: "Stars", value: "3★" }],
    badges: ["3★", "Div. 2"],
    extra: "Solved 300+ questions" },
  { platform: "CodeStudio", handle: "@pratyaydas", url: "https://www.naukri.com/code360/profile/pratyaydas",
    icon: "https://cdn.simpleicons.org/codingninjas",
    stats: [{ label: "Rating", value: "1687" },{ label: "Solved", value: "100+" }],
    badges: ["Achiever"],
    extra: "25+ badges" },
  { platform: "HackerRank", handle: "@piyadas740780", url: "https://www.hackerrank.com/profile/piyadas740780",
    icon: "https://cdn.simpleicons.org/hackerrank",
    stats: [{ label: "Solved", value: "140+" },{ label: "Cetrification", value: "Python" }],
    badges: ["5★"],
    extra: "5★ in C++ C Python · 3★ in SQL · 2+ Certificates" },
];

const experiences = [
  { role: "Data Science Intern", company: "Exemach International (OPC) Pvt. Ltd.", date: "Nov 2025 – Mar 2026",
    desc: "Built and deployed RESTful APIs for a customer-facing dashboard, reducing response time by 40%. Worked closely with the product team on feature scoping and delivery.",
    fullDesc: "Developed automated trading and backtesting systems using Python, improving strategy evaluation efficiency by 55% and enhancing the accuracy of quantitative research tools by 45%. Collaborated with cross-functional teams to build optimized web-based data visualization dashboards, improving dataset performance and usability by 40%. Utilized technologies including Python, Pandas, NumPy, Streamlit, and OpenRouter to design data-driven financial analysis and visualization solutions.",
    techStack: ["Python", "Flask", "PostgreSQL", "Matplotlib", "Seaborn","Rest API"],
    github: "https://github.com/Pratyay1219/Machine-Learning",
    certName: "Data Science Internship", certIssuer: "Exemach International (OPC) Pvt. Ltd.",
    // certLink = Google Drive link (embeds as PDF preview in popup)
    // certVerifyLink = optional issuer/verification page (shown as second button)
    certLink: "https://drive.google.com/file/d/1y3zmTn-TuMyfvEzUfkpKn5w27paRzmRQ/view?usp=sharing",
    certVerifyLink: "" },
  { role: "Web Dev Intern", company: "Gourav Go Technologies", date: "Nov 2023 – Dec 2023",
    desc: "Created responsive web pages using HTML, CSS, and JavaScript with cross-browser compatibility.",
    fullDesc: "Created responsive web pages using HTML, CSS, and JavaScript with cross-browser compatibility, and optimized both front-end and back-end components across multiple projects, reducing load times by 35%. Developed fully interactive and responsive user interfaces with multiple clickable features, improving user experience and overall application usability.",
    techStack: ["Python", "Scikit-learn", "Pandas", "NumPy", "Jupyter"],
    github: "https://github.com/Pratyay1219/Blog",
    live: "https://blog-six-lemon-46.vercel.app/",
    certName: "Web Dev Internship", certIssuer: "Gaurav Go Technologies",
    certLink: "https://drive.google.com/file/d/17p7Ouw84FITZ8V89QxfJsQEzO3CeSF2g/view?usp=sharing",
    certVerifyLink: "" },
];

const projects = [
  { name: "Market Predicter",
    shortDesc: "An educational predictive analytics project that applies machine learning and LSTM models to forecast next-day closing prices for multiple stock indices and commodities using historical time-series data.",
    fullDesc: "The objective of this project is to demonstrate an end-to-end predictive analytics workflow, including data preprocessing, exploratory data analysis (EDA), feature engineering, model development, evaluation, and comparison. The system supports multiple financial instruments and allows experimentation with different predictive models to understand their performance on real-world market data.",
    stack: ["Python", "Numpy", "Pandas", "Rest API", "Machine Learning"], live: "#", github: "https://github.com/Pratyay1219/Market-Prediction",
    ongoing: true,
    screenshots: [
      "https://images.unsplash.com/photo-1707761918029-1295034aa31e?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8c3RvY2slMjBtYXJrZXR8ZW58MHx8MHx8fDA%3D"
    ] },
  { name: "QuickBlog",
    shortDesc: "A minimalist, statically generated blog built with Next.js, Markdown, and deployed on Vercel. Perfect for sharing your thoughts, tutorials, and stories with the world.",
    fullDesc: "Developed a modern blog platform using the MERN stack that allows users to create, edit, and share articles in real time with secure authentication and a responsive interface. The platform focuses on fast performance, interactive user experience, and seamless content management. Additionally, built a minimalist statically generated blog using Next.js and Markdown, deployed on Vercel, enabling fast, SEO-friendly content publishing for tutorials, articles, and personal writing.",
    stack: ["React", "Node.js", "MongoDB", "Express", "Tailwind CSS"], live: "https://blog-six-lemon-46.vercel.app/", github: "https://github.com/Pratyay1219/Blog",
    screenshots: [
      "https://images.unsplash.com/photo-1488190211105-8b0e65b80b4e?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8YmxvZ3xlbnwwfHwwfHx8MA%3D%3D"
    ] },
  { name: "CineMatch",
    shortDesc: "A full-stack Movie Recommendation System built using FastAPI, Streamlit, and TMDB API.",
    fullDesc: "This Movie Recommendation System uses content-based filtering with TF-IDF and cosine similarity to suggest movies based on user selection. Built with FastAPI and Streamlit, it dynamically fetches movie posters and details using the TMDB API, delivering fast and interactive recommendations.",
    stack: ["Python", "Numpy", "Pandas", "Flask","TF-IDF"], live: "https://cinematch-kzy1.onrender.com/", github: "https://github.com/Pratyay1219/CineMatch",
    screenshots: [
      "https://ca-times.brightspotcdn.com/dims4/default/81d65c2/2147483647/strip/true/crop/2160x1440+0+0/resize/1200x800!/quality/75/?url=https%3A%2F%2Fcalifornia-times-brightspot.s3.amazonaws.com%2F23%2F1a%2F52fb9ef54f5bbfef556edde1b66a%2Fla-et-letterboxd-web-lead-2.jpg",
    ] },
];

const education = [
  { year: "2023 – 2027", degree: "B.Tech, Computer Science & Engineering", school: "Lovely Professional University, Phagwara -Punjab",   detail: "CGPA: 8.7 / 10.0 · Specialisation in Data Science" },
  { year: "2022 - 2023", degree: "Class XII — WBCHSE",                     school: "Hemtabad Adarsha Vidyalaya, Hemtabad - West Bengal", detail: "93.6% · 10th in State Board" },
  { year: "2020 - 2021", degree: "Class X — WBBSE",                        school: "Sarada Vidya Mandir, Hemtabad - West Bengal",        detail: "98% · School Rank #1" },
];

const achievements = [
  { title: "Institute Contest Rank #4 — CodeForces", desc: "Consistently maintained a top-4 rank among all students at university on the CodeForces platform.",             year: "2026" },
  { title: "State Board Ranker — Class XII",         desc: "Ranked 10th in the state in WBCHSE Class XII examinations across all science students.",                        year: "2023" },
  { title: "National Science Fair — First Prize",    desc: "Won first place at the National Science Fair for a project on predictive modelling in healthcare diagnostics.", year: "2018" },
];

const certifications = [
  { year: "July 2025", name: "Full-Stack Developement",        issuer: "CipherSchools",
    // certLink  → the PDF/Drive embed shown inside the popup
    // verifyLink → the Coursera/Credly verification page (open externally)
    certLink: "https://drive.google.com/file/d/161F-TGy7LnOmGwWorqP9P9CJr6sipMLV/view?usp=sharing", verifyLink: "https://www.cipherschools.com/certificate/preview?id=687d7f6e3eaa79325b2d283f" },
  { year: "April 2025", name: "Privacy and Security in Online Social Media", issuer: "NPTEL - IIT Madras",
    certLink: "https://drive.google.com/file/d/14KTq2fP2oLmc-gZkyYOwq2MDIEwlf1_e/view?usp=sharing", verifyLink: "https://archive.nptel.ac.in/content/noc/NOC25/SEM1/Ecertificates/106/noc25-cs79/Course/NPTEL25CS79S14750055904483852.pdf" },
  { year: "September 2024", name: "Fundamentals of Network Communication",   issuer: "Coursera - University of Colorado",
    certLink: "https://www.coursera.org/account/accomplishments/verify/6VYHV86DR2H9", verifyLink: "google.com/url?sa=D&q=https://www.coursera.org/account/accomplishments/verify/6VYHV86DR2H9&ust=1774625880000000&usg=AOvVaw25RHuikoHXlcYAx3KjkxBj&hl=en-GB" },
  { year: "September 2024", name: "The Bits and Bytes of Computer Networking",     issuer: "Coursera - Google",
    certLink: "https://drive.google.com/file/d/1h-AMNg2kzcKsw820het7A8qfL5JYxark/view?usp=sharing", verifyLink: "https://www.coursera.org/account/accomplishments/verify/BJ82E1BKC69K" },
];

const blogPosts = [
  { date: "Oct 2025", title: "Modernisation of financial architecture: how India is adopting stablecoins",  url: "https://blog-six-lemon-46.vercel.app/blog/68eb2b96f6a044b231c602a6" },
  { date: "Oct 2025", title: "A startup revolution, the goal of ‘innovation capital’", url: "https://blog-six-lemon-46.vercel.app/blog/68eb2c54f6a044b231c602ac" },
  { date: "Sep 2025", title: "AI being weaponised for fraud: Finance Minister Nirmala Sitharaman",url: "https://blog-six-lemon-46.vercel.app/blog/68eb2c8bf6a044b231c602ae" },
];

/* ── Certificate Popup Component ── */
function CertPopup({ certPopup, onClose }) {
  if (!certPopup) return null;

  const embedUrl = toEmbedUrl(certPopup.link || certPopup.certLink);
  const verifyUrl = certPopup.verifyLink || certPopup.certVerifyLink || null;
  const hasEmbed = embedUrl && embedUrl !== "#";
  const hasVerify = verifyUrl && verifyUrl !== "#";

  return (
    <div className="cert-box">
      <button className="popup-close" onClick={onClose}><X size={13} /></button>
      <div className="cert-icon"><Award size={22} /></div>
      <div className="cert-pop-title">{certPopup.name}</div>
      <div className="cert-pop-issuer">
        {certPopup.issuer}{certPopup.year ? ` · ${certPopup.year}` : ""}
      </div>

      {/* Certificate preview embed */}
      <div className="cert-embed-wrap">
        {hasEmbed ? (
          <iframe
            src={embedUrl}
            title={certPopup.name}
            allow="autoplay"
          />
        ) : (
          <div className="cert-embed-placeholder">
            <Award size={32} />
            <span>No preview available</span>
          </div>
        )}
      </div>

      <div className="cert-actions">
        {hasEmbed && (
          <a href={certPopup.link || certPopup.certLink} target="_blank" rel="noreferrer" className="cert-btn-pri">
            <ExternalLink size={12} /> Open full screen
          </a>
        )}
        {hasVerify && (
          <a href={verifyUrl} target="_blank" rel="noreferrer" className="cert-btn-pri" style={{ background: "var(--bg3)", color: "var(--text)" }}>
            <ExternalLink size={12} /> Verify certificate
          </a>
        )}
        {!hasEmbed && !hasVerify && (
          <span style={{ fontSize: "0.77rem", color: "var(--text-light)", fontWeight: 300 }}>
            No link provided yet.
          </span>
        )}
        <button className="cert-btn-sec" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

/* ── COMPONENT ── */
export default function Portfolio() {
  const [scrolled,      setScrolled]      = useState(false);
  const [dark,          setDark]          = useState(true);
  const [menuOpen,      setMenuOpen]      = useState(false);
  const [profilePopup,  setProfilePopup]  = useState(null);
  const [certPopup,     setCertPopup]     = useState(null);
  const [avatarPopup,   setAvatarPopup]   = useState(false);
  const [projectPopup,  setProjectPopup]  = useState(null);
  const [expPopup,      setExpPopup]      = useState(null);


  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 300);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    const fn = (e) => {
      if (e.key === "Escape") {
        setProfilePopup(null); setCertPopup(null);
        setAvatarPopup(false); setProjectPopup(null); setExpPopup(null);
      }
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, []);

  const navLinks = [
    { href: "#profiles",  label: "profiles"  },
    { href: "#skills",    label: "skills"    },
    { href: "#experience",label: "experience"},
    { href: "#projects",  label: "projects"  },
    { href: "#blog",      label: "writing"   },
    { href: "#contact",   label: "contact"   },
  ];

  return (
    <>
      <style>{style}</style>

      {/* NAV */}
      <nav>
        <div className="container nav-inner">
          <a href="#hero" className="nav-name">Pratyay Das</a>
          <div className="nav-right">
            <div className="nav-links">
              {navLinks.map(l => <a key={l.href} href={l.href}>{l.label}</a>)}
            </div>
            <button className="nav-btn" onClick={() => setDark(d => !d)} title="Toggle theme">
              {dark ? <Sun size={14} /> : <Moon size={14} />}
            </button>
            <button
              className={`hamburger-btn${menuOpen ? " open" : ""}`}
              onClick={() => setMenuOpen(o => !o)}
              aria-label="Menu"
            >
              <span /><span /><span />
            </button>
          </div>
        </div>
        <div className={`mobile-menu${menuOpen ? " open" : ""}`}>
          {navLinks.map(l => (
            <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)}>{l.label}</a>
          ))}
        </div>
      </nav>

      <div className="container">

        {/* HERO */}
        <section className="hero" id="hero">
          <div className="hero-row">
            <img className="hero-avatar"
              src="https://media.licdn.com/dms/image/v2/D5603AQGN0VT8s4RQ5Q/profile-displayphoto-scale_200_200/B56Zgqz1H1G4AY-/0/1753064889686?e=1776297600&v=beta&t=gD5PpinjhyGx_f94lucAPcxFeyRWcj4e12EXQIbU2i4"
              alt="Pratyay Das"
              onClick={() => setAvatarPopup(true)}
              role="button" tabIndex={0}
              onKeyDown={e => e.key === "Enter" && setAvatarPopup(true)} />
            <div>
              <div className="hero-name">Pratyay Das</div>
              <div className="hero-sub">CS Student · Full Stack & Data Science · Phagwara, India</div>
            </div>
          </div>
          <div className="hero-bio">
            <p>Third-year Computer Science student at <a href="https://www.lpu.in/">Lovely Professional University</a> with a strong interest in full stack development and data science. I build scalable web applications and enjoy working with data, while also exploring how systems work under the hood. Currently building NoteFlow and other web-based projects.</p>
            <p>My work mainly involves the <a href="https://www.geeksforgeeks.org/mern/understand-mern-stack/">MERN Stack</a>, databases, and data analysis, and I'm continuously learning about <a href="https://www.ibm.com/think/topics/machine-learning">Machine Learning</a> and <a href="https://www.geeksforgeeks.org/cloud-computing/cloud-computing/">Cloud Technologies</a>. I enjoy combining development and data to build practical, real-world projects.</p>
            <p>I occasionally write about <a href="https://www.ibm.com/think/topics/data-science">data science</a>, <a href="https://www.geeksforgeeks.org/web-tech/web-technology/">web development</a>, and <a href="https://codeforces.com/blog/entry/152358">competitive programming</a>. Open to <a href="#contact">internships</a>, collaborations, and interesting project opportunities.</p>
          </div>
          <div className="currently-reading">Currently reading: <em>Ichamoti - Bibhutibhusan Bandopadhay</em></div>
          <div className="hero-actions">
            <a href="https://drive.google.com/file/d/1_OXX9AZAaqpfxhKSWVygcstQrCCt6Ssx/view?usp=sharing" className="btn-resume"><Download size={12} /> Download résumé</a>
            <div className="hero-socials">
              <a href="https://github.com/Pratyay1219" title="GitHub"><FaGithub size={15} /></a>
              <a href="https://www.linkedin.com/in/pratyay19/" title="LinkedIn"><FaLinkedin size={15} /></a>
              <a href="https://x.com/PRATYAY_DAS_" title="Twitter"><FaXTwitter size={15} /></a>
              <a href="https://medium.com/@pratyay19" title="Medium"><FaMedium size={15} /></a>
              <a href="mailto:piyadas740780@gmail.com" title="Email"><Mail size={15} /></a>
              <a href="https://letterboxd.com/Pratyay_Das/" title="Letterboxd"><FaLetterboxd size={15} /></a>
              <a href="https://www.goodreads.com/pratyaydas" title="GoodReads"><FaGoodreads size={15} /></a>
            </div>
          </div>
        </section>

        <hr />

        {/* CODING PROFILES */}
        <section id="profiles">
          <div className="section-label">Coding Profiles</div>
          <div className="profiles-grid">
            {profiles.map(p => (
              <div key={p.platform} className="profile-card"
                onClick={() => setProfilePopup(p)}
                role="button" tabIndex={0}
                onKeyDown={e => e.key === "Enter" && setProfilePopup(p)}>
                <div className="profile-card-top">
                  <div className="profile-platform-row">
                    <img src={p.icon} alt={p.platform} width={13} height={13} style={{ opacity: 0.6 }} />
                    <span className="profile-platform">{p.platform}</span>
                  </div>
                  <ArrowUpRight size={11} color="var(--text-light)" />
                </div>
                <div className="profile-handle">{p.handle}</div>
                <div className="profile-stats">
                  {p.stats.map(s => (
                    <span className="profile-stat" key={s.label}><strong>{s.value}</strong> {s.label}</span>
                  ))}
                </div>
                {p.badges.length > 0 && (
                  <div className="profile-badges">
                    {p.badges.map(b => <span key={b} className={`rank-badge ${rankClass(b)}`}>{b}</span>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <hr />

        {/* SKILLS */}
        <section id="skills">
          <div className="section-label">Skills</div>
          {skillGroups.map(group => (
            <div className="skills-group" key={group.label}>
              <div className="skills-group-label">{group.label}</div>
              <div className="skills-list">
                {group.skills.map(s => <TechChip key={s.name} name={s.name} src={s.src} />)}
              </div>
            </div>
          ))}
        </section>

        <hr />

        {/* EXPERIENCE */}
        <section id="experience">
          <div className="section-label">Experience</div>
          {experiences.map(e => (
            <div className="entry" key={e.role}>
              <div>
                <div className="entry-date">{e.date}</div>
                <div className="entry-company">{e.company}</div>
              </div>
              <div>
                <div className="entry-role">{e.role}</div>
                <div className="entry-desc">{e.desc}</div>
                <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
                  <button className="entry-details-btn" onClick={() => { setExpPopup(e); }}>
                    <FileText size={11} /> view details
                  </button>
                  <button className="entry-cert"
                    onClick={() => setCertPopup({ name: e.certName, issuer: e.certIssuer, link: e.certLink, verifyLink: e.certVerifyLink })}>
                    <FileText size={11} /> view certificate
                  </button>
                </div>
              </div>
            </div>
          ))}
        </section>

        <hr />

        {/* PROJECTS */}
        <section id="projects">
          <div className="section-label">Projects</div>
          {projects.map(p => (
            <div className="project-item" key={p.name}>
              <div className="project-header">
                <div className="project-name">
                  {p.name}
                  {p.ongoing && (
                    <span className="ongoing-badge">
                      <span className="ongoing-dot"></span>
                      ongoing
                    </span>
                  )}
                </div>
                <div className="project-links">
                  {p.live && p.live !== "#" && (
                    <a href={p.live} className="project-link" target="_blank" rel="noreferrer"><ExternalLink size={11} /> live</a>
                  )}
                  {p.github && p.github !== "#" && (
                    <a href={p.github} className="project-link" target="_blank" rel="noreferrer"><FaGithub size={11} /> github</a>
                  )}
                </div>
              </div>
              <div className="project-desc">{p.shortDesc}</div>
              <div className="project-stack">
                {p.stack.map(t => <span className="stack-tag" key={t}>{t}</span>)}
              </div>
              <button className="project-details-btn" onClick={() => setProjectPopup(p)}>
                <FileText size={11} /> view details
              </button>
            </div>
          ))}
        </section>

        <hr />

        {/* EDUCATION */}
        <section id="education">
          <div className="section-label">Education</div>
          {education.map(e => (
            <div className="edu-item" key={e.degree}>
              <div className="edu-year">{e.year}</div>
              <div>
                <div className="edu-degree">{e.degree}</div>
                <div className="edu-school">{e.school}</div>
                <div className="edu-detail">{e.detail}</div>
              </div>
            </div>
          ))}
        </section>

        <hr />

        {/* ACHIEVEMENTS */}
        <section id="achievements">
          <div className="section-label">Achievements</div>
          {achievements.map(a => (
            <div className="ach-item" key={a.title}>
              <Trophy size={14} className="ach-icon" />
              <div>
                <div className="ach-title">{a.title}</div>
                <div className="ach-desc">{a.desc}</div>
                <div className="ach-year">{a.year}</div>
              </div>
            </div>
          ))}
        </section>

        <hr />

        {/* CERTIFICATIONS */}
        <section id="certifications">
          <div className="section-label">Certifications</div>
          {certifications.map(c => (
            <div className="cert-item" key={c.name}>
              <div className="cert-year">{c.year}</div>
              <div>
                <div className="cert-name">{c.name}</div>
                <div className="cert-issuer">{c.issuer}</div>
                <button className="cert-trigger" onClick={() => setCertPopup(c)}>
                  <ExternalLink size={10} /> view certificate
                </button>
              </div>
            </div>
          ))}
        </section>

        <hr />

        {/* BLOG */}
        <section id="blog">
          <div className="section-label">Writing</div>
          <p style={{ fontSize: "0.85rem", color: "var(--text-light)", marginBottom: "16px", fontWeight: 300 }}>
            Writing about ML, development, and whatever I find interesting.{" "}
            <a href="https://blog-six-lemon-46.vercel.app/" style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>All posts →</a>
          </p>
          {blogPosts.map(b => (
            <div className="blog-item" key={b.title}>
              <a href={b.url} className="blog-title">
                {b.title} <ArrowUpRight size={11} className="arrow" />
              </a>
              <div className="blog-date">{b.date}</div>
            </div>
          ))}
        </section>

        <hr />

        {/* BEYOND THE SCREEN */}
        <section id="hobbies">
          <div className="section-label">Beyond the Screen</div>
          <div className="hobbies-text">
            <p>
              I track everything I watch on <a href="https://letterboxd.com/pratyay_das/">Letterboxd</a> — a devoted cinema nerd with a soft spot for slow cinema and world films. TV shows go on <a href="https://www.serializd.com/user/Pratyay_Das/profile">Serializd</a>. Books are logged on <a href="https://www.goodreads.com/user/show/186706607-pratyay-das">Goodreads</a>. Games — backlogs, completions, and wishlists — live on <a href="https://backloggd.com/u/Pratyay_Das/">Backloggd</a>.
            </p>
            <p>
              When I'm not at a computer, you'll find me at a coffee shop with a notebook, rewatching Stalker, or going down a Wikipedia rabbit hole about something completely unrelated to CS.
            </p>
          </div>
        </section>

        <hr />

        {/* CONTACT */}
        <section id="contact">
          <div className="section-label">Contact</div>
          <p style={{ fontSize: "0.87rem", color: "var(--text-muted)", marginBottom: "18px", fontWeight: 300 }}>
            Happy to hear from people — projects, opportunities, or a good film recommendation.
          </p>
          <div className="contact-links">
            <div className="contact-row"><Mail size={13} /><a href="mailto:pratyay.das23@lpu.in">pratyay.das23@lpu.in</a></div>
            <div className="contact-row"><Phone size={13} /><span>+91 74078 05886</span></div>
            <div className="contact-row"><FaLinkedin size={13} /><a href="https://www.linkedin.com/in/pratyay19/">linkedin.com/in/pratyay19</a></div>
            <div className="contact-row"><FaGithub size={13} /><a href="https://github.com/Pratyay1219">github.com/Pratyay1219</a></div>
            <div className="contact-row"><FaXTwitter size={13} /><a href="https://x.com/PRATYAY_DAS_">@PRATYAY_DAS_</a></div>
          </div>
        </section>

      </div>

      {/* FOOTER */}
      <footer>
        <div className="container footer-inner">
          <div>
            <div className="footer-name">Pratyay Das</div>
            <div className="footer-tagline">CS Student · Full Stack & Data Science · Phagwara</div>
            <div className="footer-links">
              <a href="https://www.linkedin.com/in/pratyay19/" className="footer-link"><FaGithub size={11} /> GitHub</a>
              <a href="https://github.com/Pratyay1219" className="footer-link"><FaLinkedin size={11} /> LinkedIn</a>
              <a href="https://x.com/PRATYAY_DAS_" className="footer-link"><FaXTwitter size={11} /> Twitter</a>
              <a href="https://medium.com/@pratyay19" className="footer-link"><FaMedium size={11} /> Medium</a>
              <a href="mailto:pratyay.das23@lpu.in" className="footer-link"><Mail size={11} /> Email</a>
            </div>
          </div>
          <div className="footer-right">
            <div className="footer-copy">© {new Date().getFullYear()} Pratyay Das</div>
            <div className="footer-status"><span className="status-dot" /> Open to internships</div>
          </div>
        </div>
      </footer>

      <button className={`back-top${scrolled ? " visible" : ""}`}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
        <ChevronUp size={14} />
      </button>

      {/* ════ PROFILE POPUP ════ */}
      <div className={`popup-overlay${profilePopup ? " open" : ""}`}
        onClick={e => e.target === e.currentTarget && setProfilePopup(null)}>
        {profilePopup && (
          <div className="popup-box">
            <button className="popup-close" onClick={() => setProfilePopup(null)}><X size={13} /></button>
            <div className="popup-logo">
              <img src={profilePopup.icon} alt={profilePopup.platform} width={22} height={22} />
            </div>
            <div className="popup-plat-name">{profilePopup.platform}</div>
            <div className="popup-handle">{profilePopup.handle}</div>
            <div className="popup-stats-grid">
              {profilePopup.stats.map(s => (
                <div className="popup-stat-box" key={s.label}>
                  <div className="popup-stat-value">{s.value}</div>
                  <div className="popup-stat-label">{s.label}</div>
                </div>
              ))}
            </div>
            {profilePopup.badges.length > 0 && (
              <div className="popup-badges">
                {profilePopup.badges.map(b => (
                  <span key={b} className={`rank-badge ${rankClass(b)}`}>{b}</span>
                ))}
              </div>
            )}
            {profilePopup.extra && <p className="popup-extra">{profilePopup.extra}</p>}
            <a href={profilePopup.url} className="popup-visit" target="_blank" rel="noreferrer">
              Visit profile <ArrowUpRight size={13} />
            </a>
          </div>
        )}
      </div>

      {/* ════ CERTIFICATE POPUP ════ */}
      <div className={`cert-overlay${certPopup ? " open" : ""}`}
        onClick={e => e.target === e.currentTarget && setCertPopup(null)}>
        <CertPopup certPopup={certPopup} onClose={() => setCertPopup(null)} />
      </div>

      {/* ════ AVATAR POPUP ════ */}
      <div className={`popup-overlay${avatarPopup ? " open" : ""}`}
        onClick={e => e.target === e.currentTarget && setAvatarPopup(false)}>
        {avatarPopup && (
          <div className="popup-box avatar-popup">
            <button className="popup-close" onClick={() => setAvatarPopup(false)}><X size={13} /></button>
            <img src="https://ui-avatars.com/api/?name=Pratyay+Das&background=d4d0c8&color=444&size=128"
              alt="Pratyay Das" />
          </div>
        )}
      </div>

      {/* ════ PROJECT DETAILS POPUP ════ */}
      <div className={`popup-overlay${projectPopup ? " open" : ""}`}
        onClick={e => e.target === e.currentTarget && setProjectPopup(null)}>
        {projectPopup && (
          <div className="popup-box">
            <button className="popup-close" onClick={() => setProjectPopup(null)}><X size={13} /></button>
            <div className="project-popup-name">{projectPopup.name}</div>
            <div className="project-popup-desc">{projectPopup.fullDesc}</div>
            <div style={{ marginBottom: "14px" }}>
              <div style={{ fontSize: "0.68rem", fontWeight: "500", letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--text-light)", marginBottom: "8px" }}>Tech Stack</div>
              <div className="project-stack">
                {projectPopup.stack.map(t => <span className="stack-tag" key={t}>{t}</span>)}
              </div>
            </div>
            {projectPopup.screenshots && projectPopup.screenshots.length > 0 && (
              <div style={{ marginBottom: "18px" }}>
                <div style={{ fontSize: "0.68rem", fontWeight: "500", letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--text-light)", marginBottom: "12px" }}>Screenshots</div>
                <div className="project-screenshots">
                  {projectPopup.screenshots.map((screenshot, index) => (
                    <img key={index} src={screenshot} alt={`${projectPopup.name} screenshot ${index + 1}`} className="project-screenshot" />
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: "10px", marginTop: "18px" }}>
              {projectPopup.live && projectPopup.live !== "#" && (
                <a href={projectPopup.live} target="_blank" rel="noreferrer" className="popup-visit" style={{ flex: 1, textAlign: "center" }}>
                  <ExternalLink size={12} /> Live
                </a>
              )}
              {projectPopup.github && projectPopup.github !== "#" && (
                <a href={projectPopup.github} target="_blank" rel="noreferrer" className="popup-visit" style={{ flex: 1, textAlign: "center" }}>
                  <FaGithub size={12} /> GitHub
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ════ EXPERIENCE DETAILS POPUP ════ */}
      <div className={`popup-overlay${expPopup ? " open" : ""}`}
        onClick={e => e.target === e.currentTarget && setExpPopup(null)}>
        {expPopup && (
          <div className="popup-box">
            <button className="popup-close" onClick={() => setExpPopup(null)}><X size={13} /></button>
            <div className="exp-popup-role">{expPopup.role}</div>
            <div className="exp-popup-company">{expPopup.company} · {expPopup.date}</div>
            <div className="exp-popup-desc">{expPopup.fullDesc}</div>
            {expPopup.techStack && expPopup.techStack.length > 0 && (
              <div style={{ marginBottom: "14px" }}>
                <div style={{ fontSize: "0.68rem", fontWeight: "500", letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--text-light)", marginBottom: "8px" }}>Tech Stack</div>
                <div className="project-stack">
                  {expPopup.techStack.map(t => <span className="stack-tag" key={t}>{t}</span>)}
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: "10px", marginTop: "18px" }}>
              {expPopup.github && expPopup.github !== "#" && (
                <a href={expPopup.github} target="_blank" rel="noreferrer" className="popup-visit" style={{ flex: 1, textAlign: "center" }}>
                  <FaGithub size={12} /> GitHub
                </a>
              )}
              {expPopup.live && expPopup.live !== "#" && (
                <a href={expPopup.live} target="_blank" rel="noreferrer" className="popup-visit" style={{ flex: 1, textAlign: "center" }}>
                  <ExternalLink size={12} /> Live Demo
                </a>
              )}
              {expPopup.certLink && expPopup.certLink !== "#" && (
                <a href={expPopup.certLink} target="_blank" rel="noreferrer" className="popup-visit" style={{ flex: 1, textAlign: "center" }}>
                  <ExternalLink size={12} /> View Certificate
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
